import {
  getAccessToken,
  getRefreshToken,
  getUserInfo,
} from "@/lib/storeManager";
import { getServiceDeskUserId } from "@sd/hooks/useAuth";

function getPtsBaseUrl(): string {
  const fromEnv = (
    import.meta.env.VITE_PTS_APP_URL as string | undefined
  )?.trim();
  if (!fromEnv) {
    throw new Error("VITE_PTS_APP_URL is not set in the environment.");
  }
  return fromEnv.replace(/\/$/, "");
}

function encodeUserPayload(userInfo: unknown): string | null {
  if (!userInfo || typeof userInfo !== "object") return null;
  try {
    const json = JSON.stringify(userInfo);
    return btoa(unescape(encodeURIComponent(json)));
  } catch {
    return null;
  }
}

/**
 * Builds PTS SSO handoff URL with tokens in the hash fragment
 * (hash is not sent to the PTS server on the first request).
 */
export function buildPtsSsoHandoffUrl(): string | null {
  const access = getAccessToken();
  const refresh = getRefreshToken();
  if (!access || !refresh) return null;

  const params = new URLSearchParams();
  params.set("access", access);
  params.set("refresh", refresh);

  const uid = getServiceDeskUserId();
  if (uid) {
    params.set("uid", uid);
  }

  const userEncoded = encodeUserPayload(getUserInfo());
  if (userEncoded) {
    params.set("user", userEncoded);
  }

  return `${getPtsBaseUrl()}/sso-handoff#${params.toString()}`;
}

/**
 * Move the user from ServiceDesk into the PTS app (same tab by default).
 * Passes the current SD session so PTS can bootstrap without re-login.
 */
export function openPtsApp(options?: { sameTab?: boolean }): void {
  const sameTab = options?.sameTab !== false;
  const handoffUrl = buildPtsSsoHandoffUrl();
  const fallbackUrl = `${getPtsBaseUrl()}/employee-dashboard`;
  const target = handoffUrl ?? fallbackUrl;

  if (sameTab) {
    window.location.assign(target);
    return;
  }

  window.open(target, "_blank", "noopener,noreferrer");
}
